import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOrderDetails } from "../../services/woocommerce";
import Layout from "../../components/Layout";
import {
  Loader2, ArrowLeft, XCircle, User, PackageSearch,
  Truck, Receipt, DollarSign, ClipboardList, MapPin,
  Tag,
  PlusCircle,
  IndianRupee
} from "lucide-react";

const OrderDetailsPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderId) return;

    const fetchOrder = async () => {
      setLoading(true);
      try {
        const res = await getOrderDetails(Number(orderId));
        setOrder(res.data);
      } catch {
        setError("Unable to fetch order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <Layout>
        <div className="h-72 flex items-center justify-center text-indigo-600">
          <Loader2 className="animate-spin h-8 w-8 mr-3" />
          <span className="text-xl font-semibold">Fetching Order...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center gap-2 p-6 text-red-600">
          <XCircle className="w-6 h-6" />
          <span>{error}</span>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <p className="p-6 text-gray-700">No order found.</p>
      </Layout>
    );
  }
  console.log('$$$$$$$$', order);
  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-8">
        <div className="relative bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white p-8 rounded-3xl shadow-2xl flex justify-between items-center overflow-hidden">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-white opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 -right-10 w-60 h-60 bg-white opacity-10 rounded-full blur-3xl"></div>

          {/* Order title */}
          <h1 className="text-4xl font-extrabold flex items-center gap-3 z-10 drop-shadow-md">
            <PackageSearch className="w-10 h-10" />
            Order #{order.number}
          </h1>

          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="z-10 bg-white text-indigo-700 font-semibold px-5 py-2.5 rounded-full hover:bg-indigo-100 transition-all duration-300 ease-in-out shadow-md flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>


        <div className="bg-gradient-to-br from-white via-indigo-50 to-blue-100 border border-indigo-200 rounded-2xl shadow-lg mt-6">
          <div className="flex items-center gap-3 p-5 border-b border-indigo-100 bg-indigo-100/60 rounded-t-2xl">
            <User className="text-indigo-700" />
            <h3 className="text-lg font-semibold text-indigo-900">Customer Details</h3>
          </div>

          <div className="p-5 text-sm text-gray-800 leading-relaxed space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Name:</span>
              <span>{order.billing.first_name} {order.billing.last_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Email:</span>
              <a href={`mailto:${order.billing.email}`} className="text-indigo-600 hover:underline">
                {order.billing.email}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Phone:</span>
              <a href={`tel:${order.billing.phone}`} className="text-indigo-600 hover:underline">
                {order.billing.phone}
              </a>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-100 border border-indigo-200 rounded-2xl shadow-lg mt-6">
          <div className="flex items-center gap-3 p-5 border-b border-indigo-100 bg-indigo-100/50 rounded-t-2xl">
            <Truck className="text-indigo-700" />
            <h3 className="text-lg font-semibold text-indigo-800">Shipping Address</h3>
          </div>

          <div className="p-5 text-sm text-gray-800 leading-relaxed space-y-1">
            <p className="font-medium">{order.shipping.address_1}</p>
            <p>{order.shipping.city}, {order.shipping.state} - {order.shipping.postcode}</p>
            <p>{order.shipping.country}</p>
          </div>
        </div>


        <div className="bg-white border border-gray-200 rounded-2xl shadow-md mt-6">
          <div className="flex items-center gap-3 p-5 border-b bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-100">
            <MapPin className="text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-800">Billing Address</h3>
          </div>

          <div className="p-5 leading-relaxed text-sm text-gray-700">
            <p>{order.billing.address_1}</p>
            <p>{order.billing.city}, {order.billing.state} - {order.billing.postcode}</p>
            <p>{order.billing.country}</p>
          </div>
        </div>


        {order.customer_note && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl shadow-md mt-6">
            <div className="flex items-center gap-3 p-5 border-b border-yellow-100 bg-yellow-100/60 rounded-t-2xl">
              <ClipboardList className="text-yellow-600" />
              <h3 className="text-lg font-semibold text-yellow-800">Customer Note</h3>
            </div>
            <div className="p-5">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {order.customer_note}
              </p>
            </div>
          </div>
        )}


        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg mt-6 overflow-hidden">
          <div className="flex items-center gap-3 p-5 border-b bg-gradient-to-r from-gray-50 to-slate-100 border-gray-100">
            <Receipt className="text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-800">Order Info</h3>
          </div>

          <ul className="divide-y divide-gray-100 p-5 text-sm">
            <li className="flex justify-between py-2">
              <span className="text-gray-600">ID</span>
              <span className="font-medium">{order.id}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">Status</span>
              <span className="capitalize text-indigo-700 font-semibold">{order.status}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">Created</span>
              <span>{new Date(order.date_created).toLocaleString()}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">Order Key</span>
              <span className="text-gray-800">{order.order_key}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">Transaction ID</span>
              <span>{order.transaction_id || '—'}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">Customer ID</span>
              <span>{order.customer_id}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">Created Via</span>
              <span>{order.created_via}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">IP Address</span>
              <span>{order.customer_ip_address}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">User Agent</span>
              <span className="max-w-[50%] text-right break-words text-gray-500">{order.customer_user_agent}</span>
            </li>
            <li className="flex justify-between py-2">
              <span className="text-gray-600">Currency</span>
              <span>{order.currency_symbol} ({order.currency})</span>
            </li>
          </ul>
        </div>


        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg mt-6 overflow-hidden">
          <div className="flex items-center gap-3 p-5 border-b bg-gradient-to-r from-emerald-50 to-indigo-50 border-gray-100">
            <IndianRupee className="text-emerald-600" />
            <h3 className="text-lg font-semibold text-gray-800">Payment & Shipping</h3>
          </div>

          <ul className="divide-y divide-gray-100 p-5">
            {order.payment_method_title && (
              <li className="flex justify-between py-2">
                <span className="text-gray-600 font-medium">Payment Method</span>
                <span className="text-gray-800">{order.payment_method_title}</span>
              </li>
            )}

            {Number(order.shipping_total) > 0 && (
              <li className="flex justify-between py-2">
                <span className="text-gray-600 font-medium">Shipping</span>
                <span className="text-indigo-700 font-bold">₹{Number(order.shipping_total).toFixed(2)}</span>
              </li>
            )}

            {Number(order.discount_total) > 0 && (
              <li className="flex justify-between py-2">
                <span className="text-gray-600 font-medium">Discount</span>
                <span className="text-green-600 font-bold">− ₹{Number(order.discount_total).toFixed(2)}</span>
              </li>
            )}

            {order.fee_lines?.length > 0 &&
              order.fee_lines
                .filter((f: { total: string }) => Number(f.total) > 0)
                .map((f: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center py-2 border-b border-indigo-100 last:border-b-0"
                  >
                    <span className="text-gray-600 font-medium">{f.name}</span>
                    <span className="font-medium text-gray-900">₹{Number(f.total).toFixed(2)}</span>
                  </div>
                ))}

            {Number(order.total) > 0 && (
              <li className="flex justify-between py-2 text-lg border-t pt-3 mt-2">
                <span className="text-gray-800 font-semibold">Total</span>
                <span className="text-indigo-900 font-extrabold">₹{Number(order.total).toFixed(2)}</span>
              </li>
            )}
          </ul>

        </div>

        {order.meta_data?.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl shadow-md mt-6">
            <div className="flex items-center gap-3 p-5 border-b border-slate-100 bg-slate-100/60 rounded-t-2xl">
              <ClipboardList className="text-slate-600" />
              <h3 className="text-lg font-semibold text-slate-800">Meta Data</h3>
            </div>
            <ul className="p-5 space-y-3">
              {order.meta_data.map((meta: any, index: number) => (
                <li
                  key={index}
                  className="flex flex-col sm:flex-row sm:justify-between bg-white rounded-xl px-4 py-3 border border-slate-100 shadow-sm"
                >
                  <span className="text-gray-700 font-medium">
                    {meta.display_key || meta.key}
                  </span>
                  <span className="text-gray-600">{meta.display_value || meta.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}


        {/* {order.shipping_lines?.length > 0 &&
          order.shipping_lines.some((line: any) => Number(line.total) > 0) && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl shadow-md mt-6">
              <div className="flex items-center gap-3 p-5 border-b border-blue-100 bg-blue-100/40 rounded-t-2xl">
                <Truck className="text-blue-600" />
                <h3 className="text-lg font-semibold text-blue-700">Shipping Methods</h3>
              </div>
              <ul className="p-5 space-y-3">
                {order.shipping_lines
                  .filter((line: any) => Number(line.total) > 0)
                  .map((line: any, index: number) => (
                    <li
                      key={index}
                      className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-blue-100 shadow-sm"
                    >
                      <span className="text-gray-700 font-medium">{line.method_title}</span>
                      <span className="text-blue-700 font-bold">₹{Number(line.total).toFixed(2)}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )} */}



        {/* {order.fee_lines?.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl shadow-md mt-6">
            <div className="flex items-center gap-3 p-5 border-b border-yellow-100 bg-yellow-100/40 rounded-t-2xl">
              <DollarSign className="text-yellow-600" />
              <h3 className="text-lg font-semibold text-yellow-700">Fees</h3>
            </div>
            <ul className="p-5 space-y-3">
              {order.fee_lines.map((f: any, index: number) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-yellow-100 shadow-sm"
                >
                  <span className="text-gray-700 font-medium">{f.name}</span>
                  <span className="text-yellow-700 font-bold">₹{f.total}</span>
                </li>
              ))}
            </ul>
          </div>
        )} */}


        {/* {order.coupon_lines?.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl shadow-md mt-6">
            <div className="flex items-center gap-3 p-5 border-b border-emerald-100 bg-emerald-100/40 rounded-t-2xl">
              <ClipboardList className="text-emerald-500" />
              <h3 className="text-lg font-semibold text-emerald-700">Applied Coupons</h3>
            </div>
            <ul className="p-5 space-y-3">
              {order.coupon_lines.map((c: any, index: number) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-emerald-100 shadow-sm"
                >
                  <div className="flex flex-col">
                    <span className="text-gray-700 font-medium">Code: <span className="text-emerald-600">{c.code}</span></span>
                  </div>
                  <span className="text-emerald-700 font-bold">− ₹{c.discount}</span>
                </li>
              ))}
            </ul>
          </div>
        )} */}


        {order.refunds?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-2xl shadow-lg mt-6">
            <div className="flex items-center gap-3 p-5 border-b border-red-100 bg-red-100/40 rounded-t-2xl">
              <XCircle className="text-red-500" />
              <h3 className="text-lg font-semibold text-red-700">Refunds</h3>
            </div>
            <ul className="p-5 space-y-3">
              {order.refunds.map((r: any, index: number) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-sm border border-red-100"
                >
                  <span className="text-gray-700 font-medium">Refunded Amount</span>
                  <span className="text-red-600 font-bold">₹{r.total}</span>
                </li>
              ))}
            </ul>
          </div>
        )}


        <div className="bg-gradient-to-br from-white/70 to-indigo-50 backdrop-blur-xl border border-indigo-100 shadow-2xl rounded-3xl overflow-hidden">
          <h2 className="text-2xl font-extrabold p-6 border-b border-indigo-100 flex items-center gap-3 text-indigo-700 bg-white/60 backdrop-blur-sm">
            <PackageSearch className="text-indigo-500" />
            Order Items
          </h2>

          <ul className="divide-y divide-gray-200">
            {order.line_items.map((item: any) => (
              <li
                key={item.id}
                className="flex flex-col md:flex-row md:items-center gap-5 p-6 hover:bg-indigo-50/30 transition-all duration-200"
              >
                {item.image?.src ? (
                  <img
                    src={item.image.src}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-xl shadow-md border border-indigo-100"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded-xl flex items-center justify-center text-gray-500 font-bold shadow-inner">
                    N/A
                  </div>
                )}

                <div className="flex-1">
                  <p className="font-semibold text-gray-800 text-lg">{item.name}</p>
                  <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                  {item.meta_data?.length > 0 && (
                    <ul className="text-sm text-gray-500 mt-2 space-y-1">
                      {item.meta_data.map((meta: any) => (
                        <li key={meta.id}>
                          <span className="font-medium">{meta.display_key || meta.key}:</span>{" "}
                          {meta.display_value || meta.value}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="text-right text-xl font-bold text-indigo-700 whitespace-nowrap">
                  ₹{item.total}
                </div>
              </li>
            ))}
          </ul>
        </div>


        <div className="mt-6 p-6 bg-gradient-to-br from-indigo-50 to-white rounded-3xl shadow-lg border border-indigo-100">
          <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b pb-2 border-indigo-200 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-600" />
            Order Summary
          </h3>

          <div className="space-y-3 text-sm text-gray-700">
            {/* Subtotal */}
            {order.line_items?.length > 0 && (
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-medium text-gray-900">
                  ₹{order.line_items.reduce((sum: number, i: any) => sum + Number(i.total), 0).toFixed(2)}
                </span>
              </div>
            )}

            {/* Shipping */}
            {Number(order.shipping_total) > 0 && (
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1">
                  <Truck className="w-4 h-4 text-indigo-500" />
                  Shipping
                </span>
                <span className="font-medium text-gray-900">₹{Number(order.shipping_total).toFixed(2)}</span>
              </div>
            )}

            {/* Fees */}
            {order.fee_lines?.length > 0 &&
              order.fee_lines
                .filter((f: any) => Number(f.total) > 0)
                .map((f: any, idx: number) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="flex items-center gap-1">
                      <Tag className="w-4 h-4 text-indigo-500" />
                      {f.name}
                    </span>
                    <span className="font-medium text-gray-900">₹{Number(f.total).toFixed(2)}</span>
                  </div>
                ))}
          </div>

          {/* Total */}
          {Number(order.total) > 0 && (
            <div className="mt-4 pt-4 border-t border-indigo-100 flex justify-between items-center">
              <span className="text-lg font-semibold text-indigo-700 flex items-center gap-2">
                <PlusCircle className="w-5 h-5" />
                Total
              </span>
              <span className="text-2xl font-bold text-indigo-800">₹{Number(order.total).toFixed(2)}</span>
            </div>
          )}
        </div>


      </div>
    </Layout>
  );
};

export default OrderDetailsPage;


